// src/pages/PricingPage.jsx
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const DISPLAY = "'Playfair Display', Georgia, serif";
const SANS    = "'DM Sans', 'DM Sans', system-ui, sans-serif";
const MONO    = "'IBM Plex Mono', 'IBM Plex Mono', 'Courier New', monospace";
const BG0     = "#060a0f";
const GREEN   = "#3ddc84";
const MUTED   = "#888";
const BDR     = "#1a2a1f";

const NAV_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/faq' },
];

function TopNav() {
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, borderBottom: `1px solid ${BDR}`, background: 'rgba(6,10,15,0.95)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', height: 56 }}>
      <Link to="/" style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, textDecoration: 'none', letterSpacing: '0.05em' }}>NUVPORT OS</Link>
      <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
        {NAV_LINKS.map(l => (
          <Link key={l.href} to={l.href} style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 500, color: '#888', textDecoration: 'none' }}>{l.label}</Link>
        ))}
        <Link to="/" style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, background: GREEN, padding: '8px 20px', textDecoration: 'none', color: '#060a0f' }}>Start Free</Link>
      </div>
    </nav>
  );
}

function PageFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${BDR}`, padding: '60px 40px', background: BG0 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 40 }}>
        <div>
          <p style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, marginBottom: 8, letterSpacing: '0.05em' }}>NUVPORT OS</p>
          <p style={{ fontFamily: SANS, fontSize: '0.8125rem', color: '#555', maxWidth: 240, lineHeight: 1.6 }}>The financial intelligence environment.</p>
        </div>
        <div style={{ display: 'flex', gap: 60 }}>
          <div>
            <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: '#444', marginBottom: 16, textTransform: 'uppercase' }}>Product</p>
            {[['About', '/about'], ['Pricing', '/pricing'], ['FAQ', '/faq']].map(([label, href]) => (
              <Link key={href} to={href} style={{ display: 'block', fontFamily: SANS, fontSize: '0.875rem', color: '#888', textDecoration: 'none', marginBottom: 10 }}>{label}</Link>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: '40px auto 0', paddingTop: 32, borderTop: `1px solid ${BDR}`, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>© {new Date().getFullYear()} Nuvport OS. All rights reserved.</p>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>Zagreb, Croatia · nuvportos.com</p>
      </div>
    </footer>
  );
}

const FREE_FEATURES = [
  'Dashboard — portfolio health at a glance',
  'Portfolio Builder — add and track holdings',
  'MarketWatch — live prices and sparklines',
  'Risk Score — your exposure number',
  'Up to 5 holdings',
];

const PRO_FEATURES = [
  'Everything in Free',
  'All 21 modules unlocked',
  'Geo-Risk Map — 40+ layers, 19 conflict zones',
  'Optimizer — efficient frontier, Sharpe maximisation',
  'Backtester — historical performance vs S&P 500',
  'DCF Model — intrinsic value calculator',
  'Monte Carlo — probabilistic 10-year simulations',
  'FED Monitor and Macro Dashboard',
  'Insider Intel — 500+ Form 4 SEC filings',
  'News Sentiment — AI-scored across 7 feeds',
  'Earnings calendar and EPS estimates',
  'Unlimited holdings',
  'CSV export',
];

export default function PricingPage() {
  useEffect(() => {
    document.title = 'Pricing — Nuvport OS';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: BG0, minHeight: '100vh', color: '#ffffff' }}>
      <TopNav />

      {/* Hero */}
      <section style={{ padding: '140px 40px 80px', textAlign: 'center', maxWidth: 700, margin: '0 auto' }}>
        <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: MUTED, marginBottom: 24, textTransform: 'uppercase' }}>Pricing</p>
        <h1 style={{ fontFamily: DISPLAY, fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, marginBottom: 16, margin: '0 0 16px' }}>
          Simple. Honest. No tricks.
        </h1>
        <p style={{ fontFamily: SANS, fontSize: '1rem', color: MUTED, lineHeight: 1.7, maxWidth: 480, margin: '16px auto 0' }}>
          Bloomberg charges $24,000 a year. We charge $29 a month. Same intelligence. Different world.
        </p>
      </section>

      {/* Cards */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '0 40px 80px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

        {/* Free */}
        <div style={{ background: '#0d1117', border: `1px solid ${BDR}`, padding: 48 }}>
          <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: MUTED, marginBottom: 24, textTransform: 'uppercase' }}>Free</p>
          <div style={{ marginBottom: 32 }}>
            <span style={{ fontFamily: MONO, fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>$0</span>
            <span style={{ fontFamily: SANS, fontSize: '0.875rem', color: MUTED, marginLeft: 8 }}>forever</span>
          </div>
          <p style={{ fontFamily: SANS, fontSize: '0.875rem', color: MUTED, lineHeight: 1.6, marginBottom: 32 }}>
            Start building your portfolio and see your Risk Score. No credit card required.
          </p>
          <div style={{ borderTop: `1px solid ${BDR}`, paddingTop: 32, marginBottom: 40 }}>
            {FREE_FEATURES.map((item) => (
              <p key={item} style={{ fontFamily: SANS, fontSize: '0.875rem', color: MUTED, marginBottom: 12, paddingLeft: 16, borderLeft: `1px solid ${BDR}` }}>
                {item}
              </p>
            ))}
          </div>
          <Link to="/" style={{ display: 'block', textAlign: 'center', fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', border: '1px solid #333', padding: '14px 24px', textDecoration: 'none' }}>
            Start Free →
          </Link>
        </div>

        {/* Pro */}
        <div style={{ background: '#0d1a14', border: `1px solid ${GREEN}`, padding: 48, position: 'relative' }}>
          <div style={{ position: 'absolute', top: 20, right: 20, background: GREEN, padding: '4px 10px' }}>
            <p style={{ fontFamily: MONO, fontSize: '0.6rem', fontWeight: 600, color: '#060a0f', letterSpacing: '0.1em', margin: 0 }}>MOST POPULAR</p>
          </div>
          <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: GREEN, marginBottom: 24, textTransform: 'uppercase' }}>Pro</p>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>$29</span>
            <span style={{ fontFamily: SANS, fontSize: '0.875rem', color: MUTED, marginLeft: 8 }}>/ month</span>
          </div>
          <p style={{ fontFamily: MONO, fontSize: '0.8rem', color: MUTED, marginBottom: 32 }}>cancel anytime · no lock-in</p>
          <p style={{ fontFamily: SANS, fontSize: '0.875rem', color: MUTED, lineHeight: 1.6, marginBottom: 32 }}>
            Full access. All 21 modules. The complete intelligence environment.
          </p>
          <div style={{ borderTop: '1px solid #1a3028', paddingTop: 32, marginBottom: 40 }}>
            {PRO_FEATURES.map((item) => (
              <p key={item} style={{ fontFamily: SANS, fontSize: '0.875rem', color: item === 'Everything in Free' ? MUTED : '#cccccc', marginBottom: 12, paddingLeft: 16, borderLeft: `1px solid ${item === 'Everything in Free' ? '#1a3028' : GREEN}` }}>
                {item}
              </p>
            ))}
          </div>
          <Link to="/" style={{ display: 'block', textAlign: 'center', fontFamily: SANS, fontSize: '0.875rem', fontWeight: 700, color: '#060a0f', background: GREEN, padding: '16px 24px', textDecoration: 'none' }}>
            Start Pro — $29/month →
          </Link>
        </div>
      </section>

      {/* Comparison line */}
      <section style={{ borderTop: `1px solid ${BDR}`, padding: '60px 40px', textAlign: 'center' }}>
        <p style={{ fontFamily: SANS, fontSize: '0.875rem', color: '#555', maxWidth: 600, margin: '0 auto', lineHeight: 1.7 }}>
          Bloomberg Terminal costs $24,000/year. FactSet costs $10,000+/year. Refinitiv Workspace costs $22,000/year.
          Nuvport OS costs $348/year. Cancel anytime. No contracts. Instant access.
        </p>
      </section>

      <PageFooter />
    </div>
  );
}
